import { NOTE_DENOMS, MAX_NOTES_PER_REQUEST } from './notes.js';
import { ENC_MAX_BYTES } from './sealed.js';

/**
 * The exact shape of a signed request, checked before anything else looks at
 * it. Unknown keys, wrong types and oversized strings are refused, so no
 * user input can reach code that would throw something other than a
 * LedgerError (which would stop the sequencer), and nobody can pad the
 * public log (and its copy in Zcash memos) with junk.
 *
 * Returns an error message, or null when the shape is right. Values are
 * checked for meaning later by the ledger ops.
 */
export function requestShapeError(sreq: unknown): string | null {
  if (!isObj(sreq) || !exactKeys(sreq, ['req', 'sig'])) return 'request must have exactly req and sig';
  if (!str(sreq.sig, 128)) return 'bad sig';
  const req = sreq.req;
  if (!isObj(req) || !exactKeys(req, ['v', 'chain', 'user', 'nonce', 'ts', 'body'])) return 'req must have exactly v, chain, user, nonce, ts, body';
  if (req.v !== 1) return 'unsupported request version';
  if (!str(req.chain, 64) || !str(req.user, 64)) return 'bad chain or user';
  if (!Number.isSafeInteger(req.nonce) || !Number.isSafeInteger(req.ts)) return 'bad nonce or ts';
  const body = req.body;
  if (!isObj(body) || typeof body.type !== 'string') return 'bad body';
  const shape = BODIES[body.type];
  if (!shape) return 'unknown request type';
  if (!exactKeys(body, ['type', ...Object.keys(shape)])) return `${body.type}: unexpected or missing fields`;
  for (const [k, check] of Object.entries(shape)) if (!check(body[k])) return `${body.type}: bad ${k}`;
  return null;
}

type Check = (v: unknown) => boolean;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v) && Object.getPrototypeOf(v) === Object.prototype;
const exactKeys = (o: Record<string, unknown>, keys: string[]) => {
  const own = Object.keys(o);
  return own.length === keys.length && keys.every((k) => Object.prototype.hasOwnProperty.call(o, k));
};
/** A string of at most `max` characters (exactly `max` when exact). */
const str = (v: unknown, max: number, exact = true): v is string => typeof v === 'string' && (exact ? v.length === max : v.length <= max);
const text = (max: number): Check => (v) => typeof v === 'string' && v.length <= max;
const amount: Check = (v) => typeof v === 'string' && v.length <= 31;
const tokenId: Check = (v) => typeof v === 'string' && /^t[0-9]{1,15}$/.test(v);
const hex = (len: number): Check => (v) => str(v, len);
const list = (max: number, item: Check): Check => (v) => Array.isArray(v) && v.length >= 1 && v.length <= max && v.every(item);

const BODIES: Record<string, Record<string, Check>> = {
  register: {},
  create: { symbol: text(10), name: text(32), description: text(280), image: text(300), buyZat: amount, minTokens: amount },
  buy: { token: tokenId, zat: amount, minTokens: amount },
  sell: { token: tokenId, tokens: amount, minZat: amount },
  withdraw: { zat: amount, enc: (v) => typeof v === 'string' && v.length <= ENC_MAX_BYTES * 2, commit: hex(64) },
  note_mint: { denom: (v) => typeof v === 'string' && (NOTE_DENOMS as readonly string[]).includes(v), blinded: list(MAX_NOTES_PER_REQUEST, hex(96)) },
  note_redeem: {
    denom: (v) => typeof v === 'string' && (NOTE_DENOMS as readonly string[]).includes(v),
    notes: list(MAX_NOTES_PER_REQUEST, (n) => isObj(n) && exactKeys(n, ['serial', 'sig', 'auth']) && str(n.serial, 64) && str(n.sig, 96) && str(n.auth, 128)),
  },
};
